/**
 * The first sound this game has ever made.
 *
 * Web Audio only; every decision it plays is made by the patch table in
 * `taunt.ts` or by the server. It owns three things a pure module cannot: an
 * `AudioContext` that may only be created behind a user gesture, a listener
 * that has to be moved every frame, and a mute switch.
 *
 * Positional on purpose, and this is the whole point of the mechanic rather
 * than polish on top of it. A taunt that played at a flat volume in both ears
 * would tell a hunter that *somebody* is alive, which they already knew. A
 * taunt panned and attenuated in 3D tells them which way to walk, which is the
 * counterplay to a hider who has found a corner and stopped moving.
 */

import {
  TAUNT_HEARING_RANGE,
  aimDirection,
  dequantizeXZ,
  dequantizeY,
  type TauntMsg,
} from "@prop-hunt/shared";
import { tauntPatch } from "./taunt.js";

/**
 * Distance at which a taunt is at full volume, and the shape of the falloff
 * past it.
 *
 * `maxDistance` is `TAUNT_HEARING_RANGE` — the same constant the server culls
 * with — so the quietest taunt a client can hear is the furthest one it can be
 * sent. Two numbers here would mean either a taunt that fades to nothing before
 * the range it is culled at (silent packets, and a hunter who never learns the
 * mechanic exists) or one still at usable volume when the server stops sending
 * it, which reads as a bug in the server.
 */
const REF_DISTANCE = 4;
const ROLLOFF = 1.1;

/** Headroom under the patch gains, so four hiders at once do not clip. */
const MASTER_GAIN = 0.35;

const MUTE_KEY = "prop-hunt.muted";

export class TauntAudio {
  /**
   * Created on the first user gesture, never in the module body.
   *
   * A context constructed before a gesture starts `suspended` and browsers do
   * not resume it retroactively, so the game would be silent for the whole
   * session with nothing in the console to say why.
   */
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  /** Identity of the taunt already played, so the latch is not replayed. */
  private played: TauntMsg | null = null;
  private mutedFlag = readMuted();

  get muted(): boolean {
    return this.mutedFlag;
  }

  /**
   * Bring the audio graph up. Safe to call more than once.
   *
   * Must happen inside a user gesture — the join form's submit is the one this
   * game already has, and it is the right one: nothing before it can make a
   * noise anyway.
   */
  enable(): void {
    if (this.ctx === null) {
      // Feature-detected rather than assumed: this is the only browser API in
      // the client that a page can plausibly be loaded without, and the failure
      // mode is a thrown ReferenceError inside a form submit handler.
      if (typeof AudioContext === "undefined") return; // Play in silence.
      this.ctx = new AudioContext();
      this.master = this.ctx.createGain();
      this.master.gain.value = this.mutedFlag ? 0 : MASTER_GAIN;
      this.master.connect(this.ctx.destination);
    }
    // A context can be suspended again by the browser (a backgrounded tab), so
    // this is not only a first-run concern.
    void this.ctx.resume().catch(() => {});
  }

  /** Toggle mute, remembering it for next time. Returns the new state. */
  toggleMuted(): boolean {
    this.mutedFlag = !this.mutedFlag;
    try {
      localStorage.setItem(MUTE_KEY, this.mutedFlag ? "1" : "0");
    } catch {
      // Private browsing. The toggle still works for this session.
    }
    if (this.master !== null) this.master.gain.value = this.mutedFlag ? 0 : MASTER_GAIN;
    return this.mutedFlag;
  }

  /**
   * Move the ears to the camera.
   *
   * Called every frame with whatever the renderer is looking through, which
   * includes a found hider's free-flying spectator camera — a spectator hears
   * from where they are looking, which is the only answer that is not confusing.
   *
   * The forward vector comes from `aimDirection`, the same function the shot
   * uses, so "where the sound came from" and "where the crosshair points" are
   * derived from one definition of forward rather than two that agree until
   * somebody edits one.
   */
  setListener(x: number, y: number, z: number, yaw: number, pitch: number): void {
    const ctx = this.ctx;
    if (ctx === null) return;
    const listener = ctx.listener;
    const fwd = aimDirection(yaw, pitch, { x: 0, y: 0, z: 0 });

    // The AudioParam form rather than the deprecated `setPosition` /
    // `setOrientation` setters. Those are what the older half of the tutorials
    // use and there is no fallback to them here: every engine that ships a
    // PannerNode has exposed these as params for years, and a fallback nothing
    // reaches is a branch that will be wrong the first time it is.
    listener.positionX.value = x;
    listener.positionY.value = y;
    listener.positionZ.value = z;
    listener.forwardX.value = fwd.x;
    listener.forwardY.value = fwd.y;
    listener.forwardZ.value = fwd.z;
    listener.upX.value = 0;
    listener.upY.value = 1;
    listener.upZ.value = 0;
  }

  /**
   * Play a taunt, if it is one we have not already played.
   *
   * Takes the latched message rather than a decoded event, and compares by
   * identity, exactly as `TagHud.onTag` does: `Connection.lastTaunt` holds the
   * newest and is never cleared, so this is handed the same object every frame
   * until the next taunt lands. Two identical taunts from the same spot are
   * different objects and both play.
   *
   * A muted client still consumes the latch. Unmuting must not replay a noise
   * from thirty seconds ago as though a hider were standing there now.
   */
  onTaunt(taunt: TauntMsg | null): void {
    if (taunt === null || taunt === this.played) return;
    this.played = taunt;
    if (this.mutedFlag) return;
    this.play(
      taunt.sound,
      dequantizeXZ(taunt.xQ),
      dequantizeY(taunt.yQ),
      dequantizeXZ(taunt.zQ),
    );
  }

  /**
   * One oscillator, one envelope, one panner, all disposable.
   *
   * Nodes are built per taunt rather than pooled. A taunt is a fraction of a
   * second every twenty-odd seconds per hider; the graph is torn down by the
   * `onended` the runtime already fires, and a pool would be state to get wrong
   * for no measurable gain.
   */
  private play(sound: number, x: number, y: number, z: number): void {
    const ctx = this.ctx;
    const master = this.master;
    if (ctx === null || master === null || ctx.state !== "running") return;

    const patch = tauntPatch(sound);
    const now = ctx.currentTime;
    const duration = patch.durationMs / 1000;

    const panner = ctx.createPanner();
    panner.panningModel = "HRTF";
    panner.distanceModel = "inverse";
    panner.refDistance = REF_DISTANCE;
    panner.maxDistance = TAUNT_HEARING_RANGE;
    panner.rolloffFactor = ROLLOFF;
    panner.positionX.value = x;
    panner.positionY.value = y;
    panner.positionZ.value = z;

    const gain = ctx.createGain();
    // Ramps rather than steps at both ends: a gain that jumps from 0 produces a
    // click, and a click is a broadband transient — the one sound in this file
    // that would carry the same at every distance and defeat the attenuation
    // the whole mechanic is built on.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(patch.gain, now + 0.02);
    gain.gain.setValueAtTime(patch.gain, now + duration * 0.6);
    gain.gain.linearRampToValueAtTime(0, now + duration);

    const osc = ctx.createOscillator();
    osc.type = patch.wave;
    osc.frequency.setValueAtTime(patch.startHz, now);
    osc.frequency.linearRampToValueAtTime(patch.endHz, now + duration);

    osc.connect(gain).connect(panner).connect(master);
    osc.onended = () => {
      osc.disconnect();
      gain.disconnect();
      panner.disconnect();
    };
    osc.start(now);
    osc.stop(now + duration);
  }
}

function readMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_KEY) === "1";
  } catch {
    return false;
  }
}
